import { useState } from "react"
import {
	IconChevronReduceYOutline18 as ChevronsDownUp,
	IconChevronExpandYOutline18 as ChevronsUpDown,
} from "nucleo-ui-outline-18"

import type { TimelineEvent } from "@productlane/api/src/browser"

import { buildActivityRunRows } from "./activity-run-fold"
import { SystemEvent } from "./base/system-event"
import { getSystemEventSummaryText } from "./get-system-event-summary"

interface CollapsibleActivityRunProps {
	events: TimelineEvent[]
	hasRunAbove: boolean
	hasRunBelow: boolean
	renderEvent: (
		event: TimelineEvent,
		hasEventAbove: boolean,
		hasEventBelow: boolean,
	) => React.ReactNode
}

export function CollapsibleActivityRun({
	events,
	hasRunAbove,
	hasRunBelow,
	renderEvent,
}: CollapsibleActivityRunProps) {
	const [expanded, setExpanded] = useState(false)
	const rows = buildActivityRunRows(events, expanded)

	return (
		<>
			{rows.map((row, i) => {
				const hasAbove = i === 0 ? hasRunAbove : true
				const hasBelow = i === rows.length - 1 ? hasRunBelow : true
				if (row.kind === "toggle") {
					return (
						<ActivityRunToggle
							key="toggle"
							hidden={row.hidden}
							expanded={expanded}
							hasAbove={hasAbove}
							hasBelow={hasBelow}
							onToggle={() => setExpanded((value) => !value)}
						/>
					)
				}
				return (
					<div key={getEventKey(row.event, row.index)}>
						{renderEvent(row.event, hasAbove, hasBelow)}
					</div>
				)
			})}
		</>
	)
}

function ActivityRunToggle({
	hidden,
	expanded,
	hasAbove,
	hasBelow,
	onToggle,
}: {
	hidden: TimelineEvent[]
	expanded: boolean
	hasAbove: boolean
	hasBelow: boolean
	onToggle: () => void
}) {
	const Icon = expanded ? ChevronsDownUp : ChevronsUpDown
	const label = expanded
		? `Hide ${hidden.length} events`
		: `Show ${hidden.length} events: ${hidden.map(getSystemEventSummaryText).join(", ")}`

	return (
		<button
			type="button"
			aria-expanded={expanded}
			onClick={onToggle}
			className="group flex max-w-full text-left"
		>
			<SystemEvent
				icon={
					<Icon className="h-3 min-h-3 w-3 min-w-3 flex-none transform-gpu text-tertiary group-hover:text-secondary" />
				}
				hasEventAbove={hasAbove}
				hasEventBelow={hasBelow}
			>
				<span className="truncate text-tertiary group-hover:text-secondary">
					{label}
				</span>
			</SystemEvent>
		</button>
	)
}

function getEventKey(event: TimelineEvent, fallbackIndex: number): string {
	switch (event.type) {
		case "activity":
			return event.threadActivity.id
		case "agentActivityBurst":
			return event.burst.id
		case "comment":
			return `device-info-${event.comment.id}`
		case "live-chat-message":
			return `live-chat-${event.liveChatMessage.id}`
		default:
			return `event-${fallbackIndex}`
	}
}
